import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getApiKeys, createApiKey, deleteApiKey, formatApiError } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Key, Plus, Trash2, Loader2, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const ApiKeysPage = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    provider: 'openai',
    key: '',
    name: ''
  });

  const providers = [
    { value: 'openai', label: 'OpenAI' },
    { value: 'anthropic', label: 'Anthropic' },
    { value: 'gemini', label: 'Google Gemini' },
  ];

  const fetchApiKeys = async () => {
    try {
      const { data } = await getApiKeys();
      setApiKeys(data);
    } catch (e) {
      console.error('Failed to fetch API keys:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await createApiKey(formData);
      await fetchApiKeys();
      setDialogOpen(false);
      setFormData({ provider: 'openai', key: '', name: '' });
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!keyToDelete) return;
    try {
      await deleteApiKey(keyToDelete.id);
      setApiKeys(apiKeys.filter(k => k.id !== keyToDelete.id));
    } catch (e) {
      console.error('Failed to delete API key:', e);
    } finally {
      setDeleteDialogOpen(false);
      setKeyToDelete(null);
    }
  };

  const getProviderColor = (provider) => {
    const colors = {
      openai: '#10B981',
      anthropic: '#8B5CF6',
      gemini: '#F59E0B'
    };
    return colors[provider] || '#71717A';
  };

  return (
    <Layout>
      <div className="p-8 max-w-4xl" data-testid="api-keys-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight font-['Outfit']">API Keys</h1>
            <p className="text-muted-foreground mt-1">Manage your LLM provider API keys</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="apollo-primary-button rounded-sm gap-2" data-testid="add-api-key-button">
                <Plus className="w-4 h-4" />
                Add API Key
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add API Key</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 pt-4">
                {error && (
                  <div className="flex items-center gap-2 rounded-sm border border-destructive/25 bg-destructive/10 p-3 text-sm text-destructive">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Provider</Label>
                  <Select 
                    value={formData.provider} 
                    onValueChange={(v) => setFormData({...formData, provider: v})}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="provider-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.map(p => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Name (Optional)</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Production Key"
                    className="rounded-sm"
                    data-testid="key-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>API Key</Label>
                  <Input
                    type="password"
                    value={formData.key}
                    onChange={(e) => setFormData({...formData, key: e.target.value})}
                    placeholder="sk-..."
                    className="rounded-sm font-mono"
                    required
                    data-testid="api-key-input"
                  />
                  <p className="text-xs text-muted-foreground">Your key is encrypted and stored securely</p>
                </div>

                <Button 
                  type="submit"
                  disabled={saving || !formData.key}
                  className="apollo-primary-button w-full rounded-sm"
                  data-testid="save-api-key-button"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Key'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="apollo-panel overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Loading...</div>
          ) : apiKeys.length === 0 ? (
            <div className="p-12 text-center">
              <Key className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <h2 className="text-lg font-semibold mb-2 font-['Outfit']">No API keys</h2>
              <p className="text-muted-foreground mb-4">Add your own LLM provider API keys to use with your agents</p>
              <p className="text-sm text-muted-foreground">
                Or use the platform's shared API key (billed to your account)
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {apiKeys.map((key) => (
                <div 
                  key={key.id}
                  className="p-4 flex items-center justify-between hover:bg-secondary/50 transition-colors"
                  data-testid={`api-key-row-${key.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-10 h-10 rounded-sm flex items-center justify-center"
                      style={{ backgroundColor: `${getProviderColor(key.provider)}15` }}
                    >
                      <Key className="w-5 h-5" style={{ color: getProviderColor(key.provider) }} />
                    </div>
                    <div>
                      <p className="font-medium">{key.name || `${key.provider} Key`}</p>
                      <div className="flex items-center gap-2">
                        <span 
                          className="text-xs font-medium px-2 py-0.5 rounded-full capitalize border border-border/50 bg-background/80"
                          style={{ 
                            backgroundColor: `${getProviderColor(key.provider)}15`,
                            color: getProviderColor(key.provider)
                          }}
                        >
                          {key.provider}
                        </span>
                        <span className="text-sm text-muted-foreground font-mono">{key.key_preview}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      Added {new Date(key.created_at).toLocaleDateString()}
                    </span>
                    <button 
                      onClick={() => {
                        setKeyToDelete(key);
                        setDeleteDialogOpen(true);
                      }}
                      className="p-2 rounded-sm text-destructive transition-colors hover:bg-destructive/10"
                      data-testid={`delete-key-${key.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Delete Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete API Key</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this API key? Agents using this key will fall back to the platform key.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                variant="destructive"
                className="rounded-sm"
                data-testid="confirm-delete-key"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default ApiKeysPage;
